import AppFooter from '../components/AppFooter';
import Breadcrumbs from '../components/Breadcrumbs';
import PageMeta from '../components/PageMeta';

export default function TermsOfService() {
  return (
    <div
      className="min-h-screen bg-gradient-to-b from-orange-50 to-white dark:from-gray-900 dark:to-gray-900"
    >
      <PageMeta
        title="Terms of Service"
        description="Romy's terms of service. Understand the terms and conditions for using our AI-powered women's health companion."
        path="/terms"
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Breadcrumbs current="Terms of Service" />

        <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-2">Romy AI Chatbot &ndash; Terms and Conditions of Use</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-8"><strong>Last Updated:</strong> February 2026</p>

        <div className="prose prose-gray max-w-none space-y-6">
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            These Terms and Conditions (&ldquo;Terms&rdquo;) govern your access to and use of the Romy AI chatbot (&ldquo;Romy,&rdquo; &ldquo;Service,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;), operated by Researcher LLC DBA Romy AI (&ldquo;Company&rdquo;). By accessing, using, or interacting with Romy in any way, you agree to be bound by these Terms. If you do not agree, do not use the Service.
          </p>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              By accessing or using Romy, creating an account, submitting information, or interacting with the chatbot, you acknowledge that you have read, understood, and agreed to these Terms and our Privacy Policy. Your continued use constitutes ongoing acceptance of these Terms.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">2. Nature and Purpose of Romy</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              Romy is an artificial intelligence&ndash;powered conversational tool designed to provide general educational information, wellness-related content, and community-based insights. Romy is intended for informational and supportive purposes only.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">Romy does <strong>not</strong>:</p>
            <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-1">
              <li>Provide medical advice;</li>
              <li>Diagnose conditions;</li>
              <li>Prescribe treatment;</li>
              <li>Replace professional medical judgment;</li>
              <li>Establish a patient-provider relationship.</li>
            </ul>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">3. No Medical Advice Disclaimer</h2>

            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3">3.1 Informational Purposes Only</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              All content generated by Romy, including responses, suggestions, explanations, summaries, or references, is provided for general informational and educational purposes only and is not medical advice.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3">3.2 Not a Substitute for Professional Care</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              Romy is not a substitute for professional medical advice, diagnosis, treatment, or care. Always seek the advice of a qualified physician, licensed healthcare provider, or other qualified professional regarding any medical condition, symptoms, or treatment.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              Never disregard, delay, or modify professional medical advice because of information obtained through Romy.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3">3.3 Emergency Situations</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Romy is not designed for medical emergencies. If you believe you are experiencing a medical emergency, call 911 or your local emergency number immediately.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">4. No Physician-Patient Relationship</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Use of Romy does not create a physician-patient, therapist-client, counselor-client, or other professional relationship between you and the Company, its affiliates, employees, contractors, or partners.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">5. User Responsibility and Assumption of Risk</h2>

            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3">5.1 Personal Responsibility</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">You acknowledge and agree that:</p>
            <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-1 mb-4">
              <li>You are solely responsible for your health decisions;</li>
              <li>You use Romy at your own risk;</li>
              <li>Any actions you take based on information from Romy are your responsibility.</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3">5.2 Assumption of Risk</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              You voluntarily assume all risks associated with relying on or acting upon information provided by Romy, including risks of physical, emotional, financial, or other harm.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">6. No Guarantees or Warranties</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              Romy is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo; without warranties of any kind, express or implied.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">We do not guarantee that:</p>
            <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-1 mb-3">
              <li>Information will be accurate, complete, current, or reliable;</li>
              <li>The Service will be uninterrupted, secure, or error-free;</li>
              <li>Results will meet your expectations.</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              To the fullest extent permitted by law, we disclaim all warranties, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">7. Limitation of Liability</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              To the maximum extent permitted by law, in no event shall the Company, its officers, directors, employees, contractors, affiliates, partners, licensors, or suppliers be liable for any:
            </p>
            <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-1 mb-3">
              <li>Personal injury;</li>
              <li>Medical complications;</li>
              <li>Adverse reactions;</li>
              <li>Emotional distress;</li>
              <li>Financial loss;</li>
              <li>Data loss;</li>
              <li>Indirect, incidental, consequential, special, punitive, or exemplary damages;</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              arising out of or related to your use of, or inability to use, Romy, even if advised of the possibility of such damages.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              If liability is found despite the foregoing, our total aggregate liability shall not exceed the greater of $100 or the amount you paid to use the Service, if any, in the prior twelve (12) months.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">8. Indemnification</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              You agree to indemnify, defend, and hold harmless the Company and its affiliates, officers, directors, employees, contractors, and agents from and against any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys&apos; fees) arising from:
            </p>
            <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-1">
              <li>Your use of Romy;</li>
              <li>Your reliance on any content;</li>
              <li>Your violation of these Terms;</li>
              <li>Your violation of any law or third-party rights.</li>
            </ul>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">9. User Eligibility</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              You must be at least 13 years old to use Romy. If you are under 18, you represent that you have parental or legal guardian consent to use the Service.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              By using Romy, you represent that you meet these eligibility requirements.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">10. User Conduct</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">You agree not to:</p>
            <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-1 mb-3">
              <li>Use Romy for unlawful purposes;</li>
              <li>Submit false, misleading, or harmful information;</li>
              <li>Attempt to obtain medical diagnoses or prescriptions;</li>
              <li>Interfere with system security;</li>
              <li>Reverse engineer, copy, or exploit the Service;</li>
              <li>Harass, abuse, or harm others through the Service.</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              We reserve the right to suspend or terminate access for violations.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">11. Data, Privacy, and Security</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              Your use of Romy is subject to our Privacy Policy, which governs how we collect, use, and protect personal information.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              While we implement reasonable safeguards, no system is completely secure. You acknowledge that transmission of data carries inherent risks.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">12. Artificial Intelligence Limitations</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">You acknowledge that:</p>
            <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-1 mb-3">
              <li>Romy uses automated systems and machine learning models;</li>
              <li>Responses may be inaccurate, incomplete, biased, outdated, or inappropriate;</li>
              <li>Outputs are generated probabilistically and not verified by medical professionals;</li>
              <li>The Service may evolve over time.</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              You agree not to rely exclusively on AI-generated information for health-related decisions.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">13. Intellectual Property</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              All content, software, algorithms, designs, trademarks, logos, and materials related to Romy are owned by or licensed to the Company and are protected by applicable intellectual property laws.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              You are granted a limited, non-exclusive, non-transferable, revocable license to use Romy for personal, non-commercial purposes.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">14. Termination</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              We may suspend or terminate your access at any time, with or without notice, for any reason, including violation of these Terms.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Upon termination, your right to use the Service ceases immediately.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">15. Modifications to the Terms</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              We reserve the right to modify these Terms at any time. Updated Terms will be posted within the Service or on our website with a revised &ldquo;Last Updated&rdquo; date.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Your continued use after changes constitutes acceptance of the revised Terms.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">16. Governing Law and Dispute Resolution</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, without regard to conflict-of-law principles.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Any disputes arising out of or relating to these Terms or the Service shall be resolved exclusively in the state or federal courts located in Delaware, unless otherwise required by law.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">17. Severability</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              If any provision of these Terms is held invalid or unenforceable, the remaining provisions shall remain in full force and effect.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">18. Entire Agreement</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and the Company regarding use of Romy and supersede all prior agreements.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">19. Contact Information</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
              If you have questions about these Terms, please contact:
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Researcher LLC DBA Romy AI<br />
              16192 Coastal Highway, Lewes, Delaware 19958<br />
              Phone:{' '}
              <a href="tel:+16086160236" className="text-pink-600 dark:text-pink-400 hover:text-pink-700 dark:hover:text-pink-300 underline">
                (608) 616-0236
              </a><br />
              Email:{' '}
              <a href="mailto:hello@romyhealth.co" className="text-pink-600 dark:text-pink-400 hover:text-pink-700 dark:hover:text-pink-300 underline">
                hello@romyhealth.co
              </a>
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">20. Acknowledgment and Consent</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              By accessing or using Romy, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">You further acknowledge that:</p>
            <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-1 mb-3">
              <li>Romy does not provide medical advice;</li>
              <li>You are responsible for consulting qualified healthcare professionals;</li>
              <li>You assume all risks associated with using the Service.</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Your use of Romy constitutes your electronic signature and legal acceptance of this agreement.
            </p>
          </section>
        </div>

      </div>

      <AppFooter />
    </div>
  );
}
